import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { userService } from "@/services/userService";
import type { UpdateUserInput, User, UserRole } from "@/types/user";
import type { Organisation } from "@/types/organisation";

export const userKeys = {
  all: ["users"] as const,
  lists: () => [...userKeys.all, "list"] as const,
  list: (filters: unknown) => [...userKeys.lists(), { filters }] as const,
  details: () => [...userKeys.all, "detail"] as const,
  detail: (id: number | string | undefined) => [...userKeys.details(), id] as const,
  organisations: (id: number | string | undefined) =>
    [...userKeys.all, "organisations", id] as const,
};

export const useUsers = (options: Partial<UseQueryOptions<User[]>> = {}) => {
  return useQuery({
    queryKey: userKeys.lists(),
    queryFn: userService.getUsers,
    ...options,
  });
};

export const useUser = (
  id: number | string | undefined,
  options: Partial<UseQueryOptions<User>> = {},
) => {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => userService.getUser(id as number | string),
    enabled: !!id,
    ...options,
  });
};

export const useUserOrganisations = (
  id: number | string | undefined,
  options: Partial<UseQueryOptions<Organisation[]>> = {},
) => {
  return useQuery({
    queryKey: userKeys.organisations(id),
    queryFn: () => userService.getUserOrganisations(id as number | string),
    enabled: !!id,
    ...options,
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userService.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: UpdateUserInput }) =>
      userService.updateUser(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(variables.id) });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userService.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};

export const useUpdateUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, role }: { id: number | string; role: UserRole }) =>
      userService.updateUserRole(id, role),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(variables.id) });
    },
  });
};
